import { api } from './api';

export const batchesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getBatches: build.query({
      query: (status) => `warehouse/batches?status=${status}&limit=500`,
      transformResponse: (response) => response.batches,
      providesTags: ['Batches'],
    }),
    getBatch: build.query({
      query: (batchId) => `warehouse/batch/${batchId}`,
      // bpn_separator is a store-level setting that rides along on the
      // batch response - fold it onto the batch so the work screens can
      // read it alongside everything else they need
      transformResponse: (response) => ({
        ...response.batch,
        bpnSeparator: response.bpn_separator ?? '-',
      }),
      providesTags: (_result, _error, batchId) => [{ type: 'Batch', id: batchId }],
    }),
    // Live shipment data from BigCommerce for the pack screen - the
    // backend also returns discrepancies between the packed items and the
    // shipment's current state
    getOrderShipment: build.query({
      query: ({ batchId, orderId, shipmentId }) =>
        `warehouse/batches/${batchId}/order_shipments/${orderId}/${shipmentId}`,
      transformResponse: (response) => ({
        shipment: response.order_shipment,
        discrepancies: response.discrepancies ?? [],
        packingRules: response.packing_rules ?? { itemRules: {}, shipmentRules: [] },
        packingPhotos: response.packing_photos ?? [],
        orderStatuses: response.order_statuses ?? [],
      }),
    }),
    // Claims the batch for the session's picker. The backend re-checks that
    // the batch is still 'prepared' - a 409 means someone else got it first.
    startPicking: build.mutation({
      query: ({ batchId, cartId }) => ({
        url: `warehouse/batches/${batchId}/start_picking`,
        method: 'POST',
        body: { cart_id: cartId },
      }),
      invalidatesTags: (_result, _error, { batchId }) => ['Batches', { type: 'Batch', id: batchId }],
    }),
    // Releases the batch back to 'prepared' and removes the picker. Only
    // succeeds for the picker who claimed it.
    stopPicking: build.mutation({
      query: (batchId) => ({
        url: `warehouse/batches/${batchId}/stop_picking`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, batchId) => ['Batches', { type: 'Batch', id: batchId }],
    }),
    // Completes the pick: batch -> 'picked', and the cart's item-level pick
    // state is stored in picking_state for later rebuilds.
    markPicked: build.mutation({
      query: ({ batchId, pickedItemIds, unpickableItems }) => ({
        url: `warehouse/batches/${batchId}/picked`,
        method: 'POST',
        body: {
          picked_item_ids: pickedItemIds,
          unpickable_items: unpickableItems,
        },
      }),
      invalidatesTags: (_result, _error, { batchId }) => ['Batches', { type: 'Batch', id: batchId }],
    }),
    // Parks a batch mid-pick: the picker's progress is saved in
    // picking_state and the claim released so any user can resume it.
    parkPicking: build.mutation({
      query: ({ batchId, pickedItemIds, unpickableItems }) => ({
        url: `warehouse/batches/${batchId}/park_picking`,
        method: 'POST',
        body: {
          picked_item_ids: pickedItemIds,
          unpickable_items: unpickableItems,
        },
      }),
      invalidatesTags: (_result, _error, { batchId }) => ['Batches', { type: 'Batch', id: batchId }],
    }),
    // Claims a picking_parked batch for this session's picker. The saved
    // picking_state comes back on the response so the device can rebuild
    // the cart's progress.
    resumePicking: build.mutation({
      query: (batchId) => ({
        url: `warehouse/batches/${batchId}/resume_picking`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, batchId) => ['Batches', { type: 'Batch', id: batchId }],
    }),
    startPacking: build.mutation({
      query: (batchId) => ({
        url: `warehouse/batches/${batchId}/start_packing`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, batchId) => ['Batches', { type: 'Batch', id: batchId }],
    }),
    // Releases the batch back to 'picked' and removes the packer. Only
    // succeeds for the packer who claimed it.
    stopPacking: build.mutation({
      query: (batchId) => ({
        url: `warehouse/batches/${batchId}/stop_packing`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, batchId) => ['Batches', { type: 'Batch', id: batchId }],
    }),
    // Completes the pack: batch -> 'complete' when the cart is empty or
    // 'packed_with_returns' when picked items remain on it. The
    // per-shipment results are stored in packing_state /
    // unpackable_shipments and leftovers in return_items.
    markPacked: build.mutation({
      query: ({ batchId, packedShipmentIds, unpackableShipmentIds }) => ({
        url: `warehouse/batches/${batchId}/packed`,
        method: 'POST',
        body: {
          packed_shipment_ids: packedShipmentIds,
          unpackable_shipment_ids: unpackableShipmentIds,
        },
      }),
      invalidatesTags: (_result, _error, { batchId }) => ['Batches', { type: 'Batch', id: batchId }],
    }),
    // Parks a batch mid-pack: packed/unpackable shipment marks are saved
    // in packing_state and the packer claim released so any user can
    // resume it.
    parkPacking: build.mutation({
      query: ({ batchId, packedShipmentIds, unpackableShipmentIds, shipmentProgress }) => ({
        url: `warehouse/batches/${batchId}/park_packing`,
        method: 'POST',
        body: {
          packed_shipment_ids: packedShipmentIds,
          unpackable_shipment_ids: unpackableShipmentIds,
          shipment_progress: shipmentProgress ?? {},
        },
      }),
      invalidatesTags: (_result, _error, { batchId }) => ['Batches', { type: 'Batch', id: batchId }],
    }),
    // Claims a packing_parked batch for this session's packer. The saved
    // packing_state comes back on the response so the device can rebuild
    // which shipments were already resolved.
    resumePacking: build.mutation({
      query: (batchId) => ({
        url: `warehouse/batches/${batchId}/resume_packing`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, batchId) => ['Batches', { type: 'Batch', id: batchId }],
    }),
    // Claims a to_return batch for the session's returner. The backend
    // re-checks that the batch is still 'to_return' - a 409 means someone
    // else got it first.
    startReturning: build.mutation({
      query: (batchId) => ({
        url: `warehouse/batches/${batchId}/start_returning`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, batchId) => ['Batches', { type: 'Batch', id: batchId }],
    }),
    // Releases the batch back to 'to_return', discarding saved return
    // progress. Only succeeds for the returner who claimed it.
    stopReturning: build.mutation({
      query: (batchId) => ({
        url: `warehouse/batches/${batchId}/stop_returning`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, batchId) => ['Batches', { type: 'Batch', id: batchId }],
    }),
    // Completes the return: batch -> 'complete', and the cart's item-level
    // return state is stored in returning_state for audit.
    markReturned: build.mutation({
      query: ({ batchId, returnedItemIds }) => ({
        url: `warehouse/batches/${batchId}/returned`,
        method: 'POST',
        body: {
          returned_item_ids: returnedItemIds,
        },
      }),
      invalidatesTags: (_result, _error, { batchId }) => ['Batches', { type: 'Batch', id: batchId }],
    }),
    // Parks a batch mid-return: returned item marks are saved in
    // returning_state and the returner claim released so any user can
    // resume it.
    parkReturning: build.mutation({
      query: ({ batchId, returnedItemIds }) => ({
        url: `warehouse/batches/${batchId}/park_returning`,
        method: 'POST',
        body: {
          returned_item_ids: returnedItemIds,
        },
      }),
      invalidatesTags: (_result, _error, { batchId }) => ['Batches', { type: 'Batch', id: batchId }],
    }),
    // Claims a returning_parked batch for this session's returner. The
    // saved returning_state comes back on the response so the device can
    // rebuild which items were already returned.
    resumeReturning: build.mutation({
      query: (batchId) => ({
        url: `warehouse/batches/${batchId}/resume_returning`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, batchId) => ['Batches', { type: 'Batch', id: batchId }],
    }),
    // Applies the packer's chosen status to the order in BigCommerce -
    // offered by the change_order_status rule, never required
    updateOrderStatus: build.mutation({
      query: ({ batchId, orderId, shipmentId, statusId }) => ({
        url: `warehouse/batches/${batchId}/order_shipments/${orderId}/${shipmentId}/status`,
        method: 'POST',
        body: { status_id: statusId },
      }),
    }),
    // Registers a take_picture photo and returns a presigned PUT URL. The
    // caller compresses the image client-side and PUTs it straight to S3.
    uploadPackingPhoto: build.mutation({
      query: ({ batchId, orderShipmentId, ruleId, itemId, fileName }) => ({
        url: `warehouse/batches/${batchId}/packing_photos`,
        method: 'POST',
        body: {
          order_shipment_id: orderShipmentId,
          rule_id: ruleId,
          item_id: itemId,
          file_name: fileName,
        },
      }),
    }),
    // Removes a take_picture photo's S3 object and record - deleting the
    // last photo for a rule unchecks it in the checklist
    deletePackingPhoto: build.mutation({
      query: ({ batchId, orderId, shipmentId, photoId }) => ({
        url: `warehouse/batches/${batchId}/order_shipments/${orderId}/${shipmentId}/packing_photos/${photoId}`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const { useGetBatchesQuery, useGetBatchQuery, useGetOrderShipmentQuery, useStartPickingMutation, useStopPickingMutation, useMarkPickedMutation, useParkPickingMutation, useResumePickingMutation, useStartPackingMutation, useStopPackingMutation, useMarkPackedMutation, useParkPackingMutation, useResumePackingMutation, useStartReturningMutation, useStopReturningMutation, useMarkReturnedMutation, useParkReturningMutation, useResumeReturningMutation, useUploadPackingPhotoMutation, useDeletePackingPhotoMutation, useUpdateOrderStatusMutation } = batchesApi;
